import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Instagram, Search, Users, Heart, MessageCircle, AlertCircle, Shield, CheckCircle2, Globe, Download, Loader2, Zap, List, CheckCheck, XCircle, Clock, FileDown } from 'lucide-react';
import { motion } from 'framer-motion';
import HistorySection from '../history/HistorySection';

interface InstagramPost {
    url: string;
    thumbnail: string;
    caption: string;
    likes: number;
    comments: number;
    views?: number;
    postedAt: string;
    type: 'image' | 'video' | 'carousel';
}

interface AuditData {
    profile: {
        username: string;
        fullName: string;
        bio: string;
        profilePic: string;
        followers: number;
        following: number;
        posts: number;
        isPrivate: boolean;
        isVerified: boolean;
    };
    engagement?: {
        averageLikes: number;
        averageComments: number;
        engagementRate: number;
    };
    recentPosts?: InstagramPost[];
    deepAnalysis?: any;
    aiStrategy?: string;
}

type BulkStatus = 'pending' | 'auditing' | 'generating' | 'done' | 'error';

interface BulkJob {
    id: number;
    username: string;
    url: string;
    status: BulkStatus;
    error?: string;
}

const normalizeInstagramUrl = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return '';
    // Already a full URL
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    // Remove @ prefix if present
    const username = trimmed.replace(/^@/, '');
    return `https://instagram.com/${username}`;
};

const extractUsername = (urlOrHandle: string): string => {
    const trimmed = urlOrHandle.trim().replace(/^@/, '');
    if (trimmed.startsWith('http')) {
        try {
            const u = new URL(trimmed);
            return u.pathname.replace(/\//g, '').split('?')[0] || trimmed;
        } catch {
            return trimmed;
        }
    }
    return trimmed;
};

const statusIcon = (status: BulkStatus) => {
    switch (status) {
        case 'pending': return <Clock size={16} className="text-gray-400" />;
        case 'auditing': return <Loader2 size={16} className="text-[#216974] animate-spin" />;
        case 'generating': return <FileDown size={16} className="text-[#216974] animate-pulse" />;
        case 'done': return <CheckCheck size={16} className="text-[#54c4b6]" />;
        case 'error': return <XCircle size={16} className="text-[#216974]" />;
    }
};

const statusLabel = (status: BulkStatus) => {
    switch (status) {
        case 'pending': return 'Waiting';
        case 'auditing': return 'Auditing...';
        case 'generating': return 'Generating PDF...';
        case 'done': return 'Done';
        case 'error': return 'Failed';
    }
};

const statusColor = (status: BulkStatus) => {
    switch (status) {
        case 'pending': return 'bg-gray-50 text-gray-500';
        case 'auditing': return 'bg-[#216974]/10 text-[#216974]';
        case 'generating': return 'bg-[#216974]/10 text-[#216974]';
        case 'done': return 'bg-[#54c4b6]/10 text-[#216974]';
        case 'error': return 'bg-[#216974]/10 text-[#216974]';
    }
};

const InstagramAudit = () => {
    const [mode, setMode] = useState<'single' | 'bulk'>('single');

    // Single mode
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState('');
    const [auditData, setAuditData] = useState<AuditData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    // Bulk mode
    const [bulkInput, setBulkInput] = useState('');
    const [bulkJobs, setBulkJobs] = useState<BulkJob[]>([]);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [postsLimit, setPostsLimit] = useState<number>(12);

    const handleAudit = async () => {
        if (!url) return;
        setLoading(true);
        setError(null);
        setAuditData(null);
        setLoadingStep('Initializing Browser...');

        const steps = [
            'Navigating to Instagram...',
            'Extracting Profile Metadata...',
            'Analyzing Post Engagement...',
            'Deep Crawling Recent Content...',
            'Calculating Account Authority...'
        ];

        let stepIdx = 0;
        const interval = setInterval(() => {
            if (stepIdx < steps.length - 1) {
                setLoadingStep(steps[stepIdx++]);
            }
        }, 2000);

        try {
            const response = await fetch('/api/tools/instagram/audit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
            });

            const data = await response.json();
            if (data.success) {
                setAuditData(data);
            } else {
                let errorMsg = data.error || 'Failed to audit profile';
                if (errorMsg.includes('429') || errorMsg.toLowerCase().includes('rate limit')) {
                    errorMsg = "Instagram responded with a 'Rate Limit' block. This happens when the server makes too many requests. Please try again in 5-10 minutes.";
                }
                setError(errorMsg);
            }
        } catch (err) {
            setError('Network error. Is the server running?');
        } finally {
            clearInterval(interval);
            setLoading(false);
            setLoadingStep('');
        }
    };

    const downloadPDF = async (data: AuditData): Promise<void> => {
        const response = await fetch('/api/tools/instagram/audit-pdf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to generate PDF');
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `IG_Audit_${data.profile.username}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    };

    const handleDownloadPDF = async () => {
        if (!auditData) return;
        setExporting(true);
        try {
            await downloadPDF(auditData);
        } catch (err: any) {
            console.error('PDF Generation Error:', err);
            setError(err.message || 'Failed to generate PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const handleBulkAudit = async () => {
        const lines = bulkInput
            .split('\n')
            .map(l => l.trim())
            .filter(Boolean)
            .slice(0, 20);

        if (lines.length === 0) return;

        const jobs: BulkJob[] = lines.map((line, i) => ({
            id: i,
            username: extractUsername(line),
            url: normalizeInstagramUrl(line),
            status: 'pending',
        }));

        setBulkJobs(jobs);
        setBulkRunning(true);

        const updateJob = (id: number, patch: Partial<BulkJob>) => {
            setBulkJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));
        };

        for (const job of jobs) {
            // Audit
            updateJob(job.id, { status: 'auditing' });
            try {
                const auditRes = await fetch('/api/tools/instagram/audit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: job.url, postsLimit }),
                });
                const auditJson = await auditRes.json();

                if (!auditJson.success) {
                    let msg = auditJson.error || 'Audit failed';
                    if (msg.includes('429') || msg.toLowerCase().includes('rate limit')) {
                        msg = 'Rate limited by Instagram';
                    }
                    updateJob(job.id, { status: 'error', error: msg });
                    continue;
                }

                // Generate + download PDF
                updateJob(job.id, { status: 'generating' });
                await downloadPDF(auditJson);
                updateJob(job.id, { status: 'done' });
            } catch (err: any) {
                updateJob(job.id, { status: 'error', error: err.message || 'Unknown error' });
            }

            // Small delay between accounts to reduce rate limit risk
            await new Promise(r => setTimeout(r, 1500));
        }

        setBulkRunning(false);
    };

    const bulkDone = bulkJobs.filter(j => j.status === 'done').length;
    const bulkErrors = bulkJobs.filter(j => j.status === 'error').length;
    const bulkTotal = bulkJobs.length;

    const formatNumber = (num: number) => {
        if (!num) return '0';
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const getEngagementLevel = (rate: number) => {
        if (rate > 5) return { label: 'Excellent', color: 'text-[#54c4b6]', bg: 'bg-[#54c4b6]' };
        if (rate > 3) return { label: 'Good', color: 'text-[#216974]', bg: 'bg-[#216974]' };
        if (rate > 1.5) return { label: 'Average', color: 'text-[#e4c184]', bg: 'bg-[#e4c184]' };
        return { label: 'Low', color: 'text-[#216974]', bg: 'bg-[#216974]' };
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="text-center space-y-4">
                <div className="inline-flex p-3 bg-gradient-to-br from-[#54c4b6] to-[#216974] rounded-2xl shadow-lg shadow-[#54c4b6] animate-bounce-subtle">
                    <Instagram size={32} className="text-white" />
                </div>
                <h1 className="text-4xl font-black text-gray-900 tracking-tight">Instagram Reviewer</h1>
                <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">Analyze any public profile's engagement, audience, and health in seconds.</p>
            </div>

            {/* Mode Toggle */}
            <div className="flex justify-center">
                <div className="inline-flex bg-gray-100 rounded-2xl p-1 gap-1">
                    <button
                        onClick={() => setMode('single')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${mode === 'single' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <Search size={15} />
                        Single Account
                    </button>
                    <button
                        onClick={() => setMode('bulk')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${mode === 'bulk' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <List size={15} />
                        Bulk Audit
                        <span className="text-[10px] bg-[#216974] text-white px-2 py-0.5 rounded-full font-black uppercase tracking-wide">Up to 20</span>
                    </button>
                </div>
            </div>

            {/* SINGLE MODE */}
            {mode === 'single' && (
                <>
                    <Card className="overflow-hidden border-0 shadow-2xl shadow-[#216974]">
                        <div className="p-8 space-y-6">
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <Input
                                        placeholder="https://instagram.com/username"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        className="text-lg h-14 pl-12 rounded-2xl"
                                        leftIcon={<Search className="text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" size={20} />}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAudit()}
                                    />
                                </div>
                                <Button
                                    onClick={handleAudit}
                                    isLoading={loading}
                                    className="px-10 rounded-2xl h-14 bg-gradient-to-r from-[#216974] to-[#216974] hover:scale-105 transition-transform"
                                >
                                    Analyze
                                </Button>
                            </div>

                            {loading && (
                                <div className="flex flex-col items-center gap-3 py-4 animate-pulse">
                                    <div className="flex items-center gap-2 text-[#216974] font-bold">
                                        <Search size={18} className="animate-spin-slow" />
                                        <span>{loadingStep}</span>
                                    </div>
                                    <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                        <div className="bg-[#216974] h-full animate-progress-fast shadow-[0_0_10px_rgba(79,70,229,0.5)]"></div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 animate-in zoom-in duration-300">
                                    <AlertCircle size={20} />
                                    <p className="font-semibold">{error}</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {auditData && (
                        <div className="flex justify-end pr-4">
                            <Button
                                onClick={handleDownloadPDF}
                                isLoading={exporting}
                                className="bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 flex items-center gap-2 px-6 rounded-xl shadow-sm"
                                leftIcon={exporting ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                            >
                                Export PDF Report
                            </Button>
                        </div>
                    )}

                    {auditData && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000 p-4">
                            <div className="grid md:grid-cols-3 gap-6">
                                <Card className="md:col-span-1 border-0 shadow-xl overflow-hidden">
                                    <div className="relative h-24 bg-gradient-to-r from-[#54c4b6] via-[#216974] to-[#54c4b6]"></div>
                                    <div className="px-6 pb-8 -mt-12 flex flex-col items-center text-center">
                                        <div className="relative">
                                            <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-[#e4c184] via-red-500 to-[#216974] border-4 border-white shadow-xl flex items-center justify-center">
                                                <Instagram size={40} className="text-white" />
                                            </div>
                                            {auditData.profile.isVerified && (
                                                <div className="absolute bottom-1 right-1 bg-[#216974] p-1.5 rounded-full border-2 border-white shadow-lg">
                                                    <CheckCircle2 size={14} className="text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <h2 className="mt-4 text-xl font-bold text-gray-900">@{auditData.profile.username}</h2>
                                        <p className="text-gray-500 font-medium">{auditData.profile.fullName}</p>
                                        <p className="mt-3 text-sm text-gray-600 line-clamp-3">{auditData.profile.bio}</p>
                                    </div>
                                </Card>

                                <div className="md:col-span-2 grid grid-cols-2 gap-4">
                                    {[
                                        { label: 'Followers', value: formatNumber(auditData.profile.followers), icon: Users, color: 'text-[#216974]', bg: 'bg-[#216974]' },
                                        { label: 'Following', value: formatNumber(auditData.profile.following), icon: Users, color: 'text-[#216974]', bg: 'bg-[#216974]' },
                                        { label: 'Uploads', value: formatNumber(auditData.profile.posts), icon: Globe, color: 'text-[#216974]', bg: 'bg-[#216974]' },
                                        { label: 'Account Type', value: auditData.profile.isPrivate ? 'Private' : 'Public', icon: Shield, color: 'text-[#54c4b6]', bg: 'bg-[#54c4b6]' },
                                    ].map((stat, i) => (
                                        <Card key={i} className="border-0 shadow-lg p-6 flex flex-col justify-between hover:scale-[1.02] transition-transform">
                                            <div className={`w-10 h-10 ${stat.bg} ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                                                <stat.icon size={20} />
                                            </div>
                                            <div>
                                                <p className="text-gray-500 text-sm font-bold uppercase tracking-wider">{stat.label}</p>
                                                <p className="text-2xl font-black text-gray-900">{stat.value}</p>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>

                            {auditData.engagement ? (
                                <>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <Card className="p-8 border-0 shadow-xl overflow-hidden relative">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#216974] rounded-full -mr-16 -mt-16 opacity-50"></div>
                                            <p className="text-gray-400 text-xs font-black uppercase tracking-widest mb-4">Engagement Rate</p>
                                            <div className="flex flex-wrap items-baseline gap-2 mb-4">
                                                <span className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter">
                                                    {auditData.engagement.engagementRate}%
                                                </span>
                                                <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getEngagementLevel(auditData.engagement.engagementRate).bg} ${getEngagementLevel(auditData.engagement.engagementRate).color}`}>
                                                    {getEngagementLevel(auditData.engagement.engagementRate).label}
                                                </div>
                                            </div>
                                            <p className="text-gray-500 text-xs leading-relaxed font-medium">Against benchmark of similar sized industry accounts.</p>
                                        </Card>

                                        <Card className="p-8 border-0 shadow-xl">
                                            <p className="text-gray-400 text-xs font-black uppercase tracking-widest mb-6">Average Per Post</p>
                                            <div className="space-y-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-[#216974]/10 text-[#216974] rounded-lg">
                                                            <Heart size={18} />
                                                        </div>
                                                        <span className="font-bold text-gray-700">Likes</span>
                                                    </div>
                                                    <span className="text-xl font-black text-gray-900">{formatNumber(auditData.engagement.averageLikes)}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-[#216974]/10 text-[#216974] rounded-lg">
                                                            <MessageCircle size={18} />
                                                        </div>
                                                        <span className="font-bold text-gray-700">Comments</span>
                                                    </div>
                                                    <span className="text-xl font-black text-gray-900">{formatNumber(auditData.engagement.averageComments)}</span>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card className="p-8 border-0 bg-[#216974] text-white shadow-2xl relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                                            <p className="text-[#216974] text-[10px] font-black uppercase tracking-widest mb-2">Account Authority</p>
                                            <div className="flex items-baseline gap-1 mb-4">
                                                <span className="text-5xl font-black">
                                                    {Math.round(auditData.engagement.engagementRate * 15 > 100 ? 98 : auditData.engagement.engagementRate * 15)}
                                                </span>
                                                <span className="text-lg font-bold text-[#216974] opacity-60">/100</span>
                                            </div>
                                            <div className="overflow-hidden h-1.5 mb-4 flex rounded-full bg-white/20">
                                                <div
                                                    style={{ width: `${Math.round(auditData.engagement.engagementRate * 15 > 100 ? 98 : auditData.engagement.engagementRate * 15)}%` }}
                                                    className="bg-white rounded-full transition-all duration-1000"
                                                ></div>
                                            </div>
                                            <p className="text-[11px] text-[#216974]/90 font-medium leading-tight">
                                                Measures true niche influence by balancing audience size against active participation.
                                            </p>
                                        </Card>
                                    </div>

                                    {auditData.aiStrategy && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 30 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            className="relative group mb-10"
                                        >
                                            <div className="absolute -inset-1 bg-gradient-to-r from-[#216974] via-[#216974] to-[#216974] rounded-[2.5rem] blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                                            <Card className="relative border-0 shadow-2xl bg-white/95 backdrop-blur-3xl p-8">
                                                <div className="flex items-center gap-5 mb-8">
                                                    <div className="w-16 h-16 rounded-2xl bg-[#216974] flex items-center justify-center shadow-xl shadow-[#216974]/40">
                                                        <Zap size={32} className="text-white fill-current" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-3xl font-black text-slate-900 tracking-tight leading-none italic">AI Growth Strategist</h3>
                                                        <p className="text-sm font-black text-[#216974] mt-2 uppercase tracking-[0.2em]">Data-Driven Tactical Roadmap</p>
                                                    </div>
                                                </div>
                                                <div className="prose prose max-w-none">
                                                    <div className="bg-[#216974]/30 rounded-[2rem] p-10 border border-white shadow-inner whitespace-pre-wrap font-medium text-slate-800 leading-relaxed text-lg">
                                                        {auditData.aiStrategy}
                                                        <div className="mt-10 pt-8 border-t border-slate-200/60 flex items-center gap-3">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-[#54c4b6] animate-pulse"></div>
                                                            <span className="text-xs font-black uppercase text-slate-500 tracking-[0.3em]">Strategy Finalized • Real-Time Training Active</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </motion.div>
                                    )}

                                    {auditData.recentPosts && auditData.recentPosts.length > 0 && (
                                        <div className="space-y-6">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-2xl font-black text-gray-900 tracking-tight">Full Account Audit</h3>
                                                <span className="text-sm font-bold text-gray-400 px-4 py-2 bg-gray-50 rounded-xl">Analysis of {auditData.recentPosts.length} Found Items</span>
                                            </div>
                                            <Card className="border-0 shadow-xl overflow-hidden">
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left border-collapse">
                                                        <thead>
                                                            <tr className="bg-gray-50 border-b border-gray-100">
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest w-16">#</th>
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest whitespace-nowrap">Media</th>
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest">Link & Title</th>
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest text-center">Views</th>
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest text-center">Likes</th>
                                                                <th className="px-6 py-4 text-xs font-black uppercase text-gray-400 tracking-widest text-center">Comments</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-50">
                                                            {auditData.recentPosts.map((post, idx) => (
                                                                <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                                                                    <td className="px-6 py-4 text-sm font-black text-gray-300">{idx + 1}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        <a href={post.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-tr from-[#e4c184] via-red-500 to-[#216974] shadow-lg hover:rotate-12 transition-transform">
                                                                            <Instagram size={24} className="text-white" />
                                                                        </a>
                                                                    </td>
                                                                    <td className="px-6 py-4 min-w-[300px]">
                                                                        <div className="flex flex-col">
                                                                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-900 hover:text-[#216974] transition-colors line-clamp-2 leading-snug">
                                                                                {post.caption || 'Untitled Clip / Post'}
                                                                            </a>
                                                                            <span className="text-[10px] font-bold text-[#216974] uppercase tracking-widest mt-1">View on Instagram</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-center">
                                                                        <span className="text-sm font-black text-gray-900">{post.views !== undefined ? formatNumber(post.views) : '-'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-center">
                                                                        <div className="flex items-center justify-center gap-1.5 text-[#216974] font-bold">
                                                                            <Heart size={14} className="fill-current" />
                                                                            <span className="text-gray-900">{formatNumber(post.likes)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-center">
                                                                        <div className="flex items-center justify-center gap-1.5 text-[#216974] font-bold">
                                                                            <MessageCircle size={14} className="fill-current" />
                                                                            <span className="text-gray-900">{formatNumber(post.comments)}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Card>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="p-10 bg-gradient-to-br from-[#e4c184] to-[#e4c084] border border-[#e4c184] rounded-[2rem] flex items-center gap-6 text-[#e4c184] shadow-xl shadow-[#e4c184]/50">
                                    <Shield size={48} className="text-[#216974]" />
                                    <div>
                                        <h3 className="font-black text-2xl tracking-tight mb-2">Detailed Analytics Locked</h3>
                                        <p className="font-bold text-lg opacity-80 leading-snug">This account is private. Engagement metrics and content analysis require a public profile for accurate retrieval.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* BULK MODE */}
            {mode === 'bulk' && (
                <div className="space-y-6">
                    <Card className="overflow-hidden border-0 shadow-2xl shadow-[#216974]">
                        <div className="p-8 space-y-5">
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-black text-gray-700 uppercase tracking-widest">
                                        Paste Usernames or URLs — One Per Line (max 20)
                                    </label>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap">Posts per account</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={50}
                                            value={postsLimit}
                                            onChange={e => setPostsLimit(Math.max(1, Math.min(50, parseInt(e.target.value) || 12)))}
                                            disabled={bulkRunning}
                                            className="w-16 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold text-center text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#54c4b6] disabled:opacity-50"
                                        />
                                    </div>
                                </div>
                                <textarea
                                    value={bulkInput}
                                    onChange={e => setBulkInput(e.target.value)}
                                    disabled={bulkRunning}
                                    rows={10}
                                    placeholder={`instagram.com/username1\ninstagram.com/username2\n@username3\nusername4\nhttps://instagram.com/username5\n...`}
                                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800 placeholder-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent transition disabled:opacity-50"
                                />
                                <p className="mt-2 text-xs text-gray-400 font-medium">
                                    {bulkInput.split('\n').filter(l => l.trim()).length} / 20 accounts entered
                                </p>
                            </div>

                            <Button
                                onClick={handleBulkAudit}
                                isLoading={bulkRunning}
                                disabled={bulkRunning || !bulkInput.trim()}
                                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#216974] to-[#216974] hover:scale-[1.01] transition-transform text-lg font-black"
                                leftIcon={<Download size={20} />}
                            >
                                {bulkRunning ? `Auditing... (${bulkDone}/${bulkTotal} done)` : 'Start Bulk Audit & Download PDFs'}
                            </Button>

                            {bulkRunning && (
                                <div className="space-y-1.5">
                                    <div className="flex justify-between text-xs font-bold text-gray-500 mb-2">
                                        <span>{bulkDone} completed</span>
                                        {bulkErrors > 0 && <span className="text-[#216974]">{bulkErrors} failed</span>}
                                        <span>{bulkTotal - bulkDone - bulkErrors} remaining</span>
                                    </div>
                                    <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#216974] to-[#216974] rounded-full transition-all duration-500"
                                            style={{ width: `${bulkTotal > 0 ? ((bulkDone + bulkErrors) / bulkTotal) * 100 : 0}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>

                    {bulkJobs.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                        >
                            <div className="flex items-center justify-between px-1">
                                <h3 className="text-lg font-black text-gray-900">Audit Queue</h3>
                                {!bulkRunning && bulkDone > 0 && (
                                    <span className="text-sm font-bold text-[#54c4b6] flex items-center gap-1.5">
                                        <CheckCheck size={16} />
                                        {bulkDone} PDF{bulkDone > 1 ? 's' : ''} downloaded
                                    </span>
                                )}
                            </div>
                            <Card className="border-0 shadow-xl overflow-hidden">
                                <div className="divide-y divide-gray-50">
                                    {bulkJobs.map((job, idx) => (
                                        <div key={job.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/50 transition-colors">
                                            <span className="text-xs font-black text-gray-300 w-6 text-right">{idx + 1}</span>
                                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-[#e4c184] via-red-500 to-[#216974] flex items-center justify-center flex-shrink-0">
                                                <Instagram size={16} className="text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-gray-900 text-sm truncate">@{job.username}</p>
                                                {job.status === 'done' && (
                                                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                                                        IG_Audit_{job.username}_{new Date().toISOString().split('T')[0]}.pdf
                                                    </p>
                                                )}
                                                {job.error && (
                                                    <p className="text-[10px] text-[#216974] font-medium mt-0.5 truncate">{job.error}</p>
                                                )}
                                            </div>
                                            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold flex-shrink-0 ${statusColor(job.status)}`}>
                                                {statusIcon(job.status)}
                                                {statusLabel(job.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>

                            {!bulkRunning && bulkJobs.length > 0 && (
                                <div className="flex justify-center pt-2">
                                    <button
                                        onClick={() => { setBulkJobs([]); setBulkInput(''); }}
                                        className="text-sm text-gray-400 hover:text-gray-600 font-bold transition-colors"
                                    >
                                        Clear & Start Over
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>
            )}

            {/* History section - always visible at bottom */}
            <HistorySection toolId="ig-audit" />
        </div>
    );
};

export default InstagramAudit;
